import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ShieldCheck, User, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';

const ROLES = ['admin','ketua_rt','wakil','sekretaris','bendahara','sie_keamanan','sie_kebersihan','sie_pemuda','sie_perlengkapan','sie_umum','warga'];
const ROLE_LABELS = {
    admin:'Administrator', ketua_rt:'Ketua RT', wakil:'Wakil RT', sekretaris:'Sekretaris',
    bendahara:'Bendahara', sie_keamanan:'Sie Keamanan', sie_kebersihan:'Sie Kebersihan',
    sie_pemuda:'Sie Pemuda', sie_perlengkapan:'Sie Perlengkapan', sie_umum:'Sie Umum', warga:'Warga'
};
const ROLE_COLORS = {
    admin:'bg-red-100 text-red-700', ketua_rt:'bg-blue-100 text-blue-700',
    wakil:'bg-sky-100 text-sky-700', sekretaris:'bg-purple-100 text-purple-700',
    bendahara:'bg-green-100 text-green-700', warga:'bg-slate-100 text-slate-600'
};

const emptyForm = { username: '', password: '', name: '', role: 'warga', phone: '', nik: '', citizen_id: '' };

export default function UsersPage() {
    const { user: currentUser, hasRole } = useAuth();
    const { t } = useLang();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [citizens, setCitizens] = useState([]);
    const [citizenSearch, setCitizenSearch] = useState('');
    const [showCitizenPicker, setShowCitizenPicker] = useState(false);
    const canManage = hasRole('ketua_rt', 'wakil');

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/users`, { withCredentials: true });
            setUsers(res.data);
        } catch { toast.error('Gagal memuat data pengguna'); }
        setLoading(false);
    };

    const loadCitizens = async (q = '') => {
        try {
            const res = await axios.get(`${API}/citizens?q=${q}&limit=20`, { withCredentials: true });
            setCitizens(res.data.data || []);
        } catch {}
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { if (showCitizenPicker) loadCitizens(citizenSearch); }, [citizenSearch, showCitizenPicker]);

    const openAdd = () => { setEditData(null); setForm(emptyForm); setShowForm(true); };
    const openEdit = (u) => {
        setEditData(u);
        setForm({ username: u.username || '', password: '', name: u.name, role: u.role, phone: u.phone || '', nik: u.nik || '', citizen_id: u.citizen_id || '' });
        setShowForm(true);
    };

    const selectCitizen = (c) => {
        setForm(f => ({
            ...f,
            name: c.head_of_family,
            phone: c.phone || f.phone,
            nik: c.members?.[0]?.nik || f.nik,
            citizen_id: c.id,
        }));
        setShowCitizenPicker(false);
        toast.success(`Data warga "${c.head_of_family}" ditautkan`);
    };

    const handleSave = async () => {
        if (!form.name || !form.username || (!editData && !form.password)) {
            toast.error('Isi nama, username, dan password'); return;
        }
        setSaving(true);
        try {
            if (editData) {
                const upd = { name: form.name, role: form.role, phone: form.phone, nik: form.nik, citizen_id: form.citizen_id };
                if (form.password) upd.password = form.password;
                await axios.put(`${API}/users/${editData.id}`, upd, { withCredentials: true });
                toast.success('Pengguna diperbarui');
            } else {
                await axios.post(`${API}/users`, form, { withCredentials: true });
                toast.success('Pengguna baru ditambahkan');
            }
            setShowForm(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
        setSaving(false);
    };

    const toggleActive = async (u) => {
        try {
            await axios.put(`${API}/users/${u.id}`, { is_active: !u.is_active }, { withCredentials: true });
            toast.success(u.is_active ? 'Pengguna dinonaktifkan' : 'Pengguna diaktifkan');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Hapus pengguna ini?')) return;
        try { await axios.delete(`${API}/users/${id}`, { withCredentials: true }); toast.success('Dihapus'); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Gagal menghapus'); }
    };

    const filteredCitizens = citizens.filter(c =>
        c.head_of_family?.toLowerCase().includes(citizenSearch.toLowerCase()) ||
        c.kk_number?.includes(citizenSearch)
    );

    return (
        <div className="space-y-5" data-testid="users-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl">{t.usersTitle}</h1>
                    <p className="text-sm text-slate-500 mt-1">{users.length} pengguna terdaftar</p>
                </div>
                {canManage && (
                    <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-user-btn">
                        <Plus className="w-4 h-4" /> {t.addUser}
                    </Button>
                )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="font-semibold text-slate-700">{t.name}</TableHead>
                                <TableHead className="font-semibold text-slate-700 hidden md:table-cell">Username</TableHead>
                                <TableHead className="font-semibold text-slate-700">{t.role}</TableHead>
                                <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">{t.phone}</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-center">{t.status}</TableHead>
                                {canManage && <TableHead className="font-semibold text-slate-700 text-right">{t.actions}</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? [...Array(5)].map((_, i) => (
                                <TableRow key={i}>{[...Array(6)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                            )) : users.map(u => (
                                <TableRow key={u.id} className="hover:bg-slate-50 transition-colors">
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ROLE_COLORS[u.role] || 'bg-slate-100'}`}>
                                                {u.role === 'admin' ? <ShieldCheck className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-slate-800 text-sm">{u.name}</p>
                                                <p className="text-xs text-slate-400 md:hidden">{u.username}</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell text-sm text-slate-600 font-mono">{u.username}</TableCell>
                                    <TableCell>
                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-600'}`}>
                                            {ROLE_LABELS[u.role] || u.role}
                                        </span>
                                    </TableCell>
                                    <TableCell className="hidden lg:table-cell text-sm text-slate-500">{u.phone || '—'}</TableCell>
                                    <TableCell className="text-center">
                                        {canManage && u.id !== currentUser?.id ? (
                                            <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)}
                                                data-testid={`toggle-active-${u.id}`} />
                                        ) : (
                                            <span className={`text-xs px-2 py-1 rounded-full ${u.is_active !== false ? 'badge-active' : 'badge-inactive'}`}>
                                                {u.is_active !== false ? t.active : t.inactive}
                                            </span>
                                        )}
                                    </TableCell>
                                    {canManage && (
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600 transition-colors"
                                                    data-testid={`edit-user-${u.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                {u.id !== currentUser?.id && (
                                                    <button onClick={() => handleDelete(u.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500 transition-colors"
                                                        data-testid={`delete-user-${u.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Add/Edit User Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editData ? 'Edit Pengguna' : t.addUser}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {/* Citizen auto-fill */}
                        {canManage && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                                <p className="text-xs font-semibold text-blue-800 mb-2">Auto-isi dari Data Warga</p>
                                <Button type="button" variant="outline" size="sm" onClick={() => { setShowCitizenPicker(true); loadCitizens(''); }}
                                    className="w-full gap-2 text-blue-700 border-blue-300 hover:bg-blue-50 text-xs h-8" data-testid="pick-citizen-btn">
                                    <Search className="w-3.5 h-3.5" />
                                    {form.citizen_id ? `Warga terpilih: ${form.name}` : 'Pilih dari data warga'}
                                </Button>
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label>{t.name} *</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="user-name-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Username *</Label>
                            <Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                                placeholder="Username untuk login" disabled={!!editData} data-testid="user-username-input" />
                            {editData && <p className="text-xs text-slate-400">Username tidak bisa diubah setelah dibuat</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>{editData ? 'Password Baru (kosongkan jika tidak diubah)' : `${t.password} *`}</Label>
                            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                placeholder={editData ? 'Kosongkan jika tidak diubah' : 'Password'} data-testid="user-password-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.role}</Label>
                            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.phone}</Label>
                            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.nik}</Label>
                            <Input value={form.nik} onChange={e => setForm(f => ({ ...f, nik: e.target.value }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>{t.cancel}</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-user-btn">
                            {saving ? 'Menyimpan...' : t.save}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Citizen picker dialog */}
            <Dialog open={showCitizenPicker} onOpenChange={setShowCitizenPicker}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Pilih Data Warga</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Cari nama atau No. KK..." value={citizenSearch}
                            onChange={e => setCitizenSearch(e.target.value)} data-testid="citizen-search-input" />
                        <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
                            {filteredCitizens.length === 0 ? (
                                <div className="py-8 text-center text-slate-400 text-sm">Tidak ada data ditemukan</div>
                            ) : filteredCitizens.map(c => (
                                <button key={c.id} onClick={() => selectCitizen(c)}
                                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`citizen-option-${c.id}`}>
                                    <p className="font-semibold text-slate-800 text-sm">{c.head_of_family}</p>
                                    <p className="text-xs text-slate-500">KK: {c.kk_number} · {c.address}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
